import { useMutation, useQuery } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";

import { ApiError } from "../../api/client";
import { oauthApi, type ConnectRequestView } from "../../api/oauth";

const BLOCKED: Record<string, string> = {
  submitted: "Your application is already submitted, so there's nothing left for an agent to do.",
  withdrawn: "This application is closed.",
  limit: "You've reached today's limit for connecting an agent. Try again tomorrow.",
};

function NeedsCv({ returnPath }: { returnPath: string }) {
  return (
    <>
      <p>Add your CV first. Your agent fills your profile from it.</p>
      <p>
        <Link className="button" to={`/apply?connect=${encodeURIComponent(returnPath)}`}>
          Add your CV
        </Link>
      </p>
    </>
  );
}

function Allow({ blob, view }: { blob: string; view: ConnectRequestView }) {
  const decide = useMutation({
    mutationFn: (allow: boolean) => oauthApi.decide(blob, allow),
    onSuccess: ({ redirect_url }) => window.location.assign(redirect_url),
  });
  return (
    <>
      <p>
        <strong>{view.client_name}</strong> wants to connect to your Tekdi Apply application. It
        will be able to read and fill your profile and give you the link to the questions.
      </p>
      <p className="muted">
        You'll go back to <strong>{view.redirect_target}</strong>. The connection lasts 72 hours.
        {view.replaces_connection && " It replaces the agent you connected before."}
      </p>
      {decide.isError && <p role="alert">{decide.error.message}</p>}
      <div className="actions">
        <button type="button" disabled={decide.isPending} onClick={() => decide.mutate(true)}>
          Allow
        </button>
        <button
          type="button"
          className="secondary"
          disabled={decide.isPending}
          onClick={() => decide.mutate(false)}
        >
          Deny
        </button>
      </div>
    </>
  );
}

export function OAuthConsentPage() {
  const [params] = useSearchParams();
  const blob = params.get("request") ?? "";
  const returnPath = `/oauth/consent?request=${encodeURIComponent(blob)}`;
  const view = useQuery({
    queryKey: ["oauth-request", blob],
    queryFn: () => oauthApi.view(blob),
    enabled: Boolean(blob),
    retry: false,
  });

  let body: React.ReactNode;
  if (!blob || view.isError) {
    body = (
      <p role="alert">
        {view.error instanceof ApiError
          ? view.error.message
          : "This link is incomplete. Start connecting again from your AI app."}
      </p>
    );
  } else if (view.isPending) {
    body = <p>Loading…</p>;
  } else if (!view.data.signed_in) {
    body = (
      <>
        <p>
          <strong>{view.data.client_name}</strong> wants to connect to Tekdi Apply. Sign in to your
          application first.
        </p>
        <p>
          <Link className="button" to={`/consent?next=${encodeURIComponent(returnPath)}`}>
            Sign in to continue
          </Link>
        </p>
      </>
    );
  } else if (view.data.block === "needs_cv") {
    body = <NeedsCv returnPath={returnPath} />;
  } else if (view.data.block) {
    body = <p role="alert">{BLOCKED[view.data.block] ?? "You can't connect an agent now."}</p>;
  } else {
    body = <Allow blob={blob} view={view.data} />;
  }

  return (
    <main className="page">
      <h1>Connect your AI agent</h1>
      {body}
    </main>
  );
}
